import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { cueCardsApi, type CueMode, type SessionCard } from '@/api/cueCards';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { cn } from '@/lib/utils';
import { answerChecked, tapSelection } from '@/lib/haptics';
import { enqueue } from '@/lib/outbox';
import { hasWordVariants, isWordAnswerCorrect } from '@/lib/wordAnswer';
import { playSrc } from '@/lib/wordAudio';

const MODE_KEY: Record<string, string> = {
  card: 'cueTrainer.modeCard',
  choose: 'cueTrainer.modeChoose',
  sentence: 'cueTrainer.modeSentence',
  audio: 'cueTrainer.modeAudio',
  type: 'cueTrainer.modeType',
};

// Ключи подписей части речи для указателя значения.
const POS_KEY: Record<string, string> = {
  noun: 'cueTrainer.posNoun',
  verb: 'cueTrainer.posVerb',
  adjective: 'cueTrainer.posAdjective',
  adverb: 'cueTrainer.posAdverb',
  phrase: 'cueTrainer.posPhrase',
  pronoun: 'cueTrainer.posPronoun',
  preposition: 'cueTrainer.posPreposition',
  conjunction: 'cueTrainer.posConjunction',
};
const posKey = (pos?: string) => (pos ? POS_KEY[pos.toLowerCase()] : '');

// Sense indicator — shown only for polysemous words, so the student knows WHICH
// meaning is being drilled (crucial for "choose": live→жить vs живой).
function SenseTag({ card }: { card: SessionCard }) {
  const { t } = useTranslation();
  if (card.sense_total <= 1) return null;
  // Часть речи может прийти незнакомая — тогда показываем как есть, без ключа.
  const key = posKey(card.pos);
  const pos = key ? t(key) : (card.pos ?? '');
  return (
    <span className="mt-2 inline-flex items-center gap-1.5 rounded-pill bg-surface-container-low px-2.5 py-0.5 text-xs font-semibold text-on-surface-variant">
      {pos && <span className="uppercase tracking-label">{pos}</span>}
      {card.ipa && <span className="font-mono text-on-surface-variant/80">{card.ipa}</span>}
      <span className="text-on-surface-variant/70">{t('cueTrainer.sense', { n: card.sense_index + 1, total: card.sense_total })}</span>
    </span>
  );
}

/**
 * CueCardsTrainerPage — the adaptive 5-mode vocabulary trainer. It pulls a batch
 * of cards from the server (each already carries the mode chosen by the word's
 * memory strength) and plays them one by one. Grading goes through the existing
 * SRS /review endpoint; the server reschedules the word. When a batch is done we
 * fetch the next; an empty batch means the session is complete.
 */
/** Карточка очереди со своим неизменным ключом — см. loadBatch. */
type QueuedCard = SessionCard & { uid: string };

export function CueCardsTrainerPage() {
  const { t } = useTranslation();
  const { setId } = useParams<{ setId: string }>();
  const navigate = useNavigate();
  // setId === 'my' → train the student's OWN saved words (personal dictionary),
  // not a curator set. Back then returns to the vocabulary page.
  const isPersonal = setId === 'my';
  // Способ и заход берём из адреса: «?mode=audio» — тренируем только «на слух»,
  // «?scope=learned» — повторяем уже выученное. Пусто — всё несданное подряд,
  // как было раньше.
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const mode = params.get('mode') as CueMode | null;
  const learnedScope = params.get('scope') === 'learned';
  const backTo = isPersonal ? '/app/vocabulary' : '/app/cue-cards';
  // Порядковый номер партии: карточки повторного показа не должны получить
  // ключ уже показанной карточки.
  const batchSeq = useRef(0);
  const [queue, setQueue] = useState<QueuedCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [stats, setStats] = useState({ answered: 0, correct: 0 });

  const loadBatch = async () => {
    if (!setId) return;
    setLoading(true);
    try {
      const opts = { mode: mode ?? undefined, learned: learnedScope };
      const res = isPersonal
        ? await cueCardsApi.getPersonalSession(15, opts)
        : await cueCardsApi.getSession(setId, 15, opts);
      if (res.items.length === 0) setFinished(true);
      // Каждой карточке — свой неизменный ключ. Раньше ключ собирался из
      // слова, режима и ДЛИНЫ очереди, и у многозначных слов это давало
      // совпадение: «school» в значении «школа» и «school» в значении
      // «учить» шли подряд, ключ выходил одинаковым, React не пересоздавал
      // компонент — и состояние «уже отвечено» перетекало на следующую
      // карточку. Она открывалась с подсвеченным ответом и заблокированными
      // кнопками. Ровно так тренажёр и зависал.
      else setQueue(res.items.map((it, i) => ({ ...it, uid: `${batchSeq.current}-${i}` })));
      batchSeq.current += 1;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadBatch();   }, [setId, mode, learnedScope]);

  const current = queue[0];

  // ⚠️ `typed` — то, что ученик НАБРАЛ, а не наш вердикт о нём. Вердикт по нему
  // выносит СЕРВЕР (`vocab-service`, gradeTypedAnswer), наш `quality` при этом
  // отбрасывается. Своя сверка ниже остаётся ради мгновенного отклика на
  // экране: ждать сеть, чтобы показать галочку, — плохо, а расходиться им
  // нельзя, и стережёт это общий набор векторов `word`.
  const grade = (correct: boolean, typed?: string) => {
    if (!current) return;
    answerChecked(correct);
    // quality: 5 = perfect recall, 2 = failed (SM2 resets the box on q<3).
    // sense_index grades THIS meaning independently (polysemous words).
    // Через очередь: без сети повторение слова раньше пропадало молча, и
    // тренажёр «забывал» всё, что ученик выучил в дороге.
    // ⚠️ СПОСОБ ВХОДИТ В КЛЮЧ ОЧЕРЕДИ. Ключ отсеивает повторы одного и того же
    // действия; без способа ответы «карточка» и «на слух» по одному слову
    // считались бы одним действием, и второй молча пропал бы — особенно на
    // плохой связи, ради которой очередь и заведена.
    void enqueue(
      'vocab-review',
      `vocab:${current.word_id}:${current.sense_index ?? 0}:${current.mode}`,
      {
        entry_id: current.word_id,
        sense_index: current.sense_index,
        quality: correct ? 5 : 2,
        mode: current.mode,
        typed,
      },
    );
    // ⚠️ СБРАСЫВАЕМ КЕШ СЛОВАРЯ. Числа на карточке словаря — «Выучено» и бейджи
    // «освоено из всех» — живут в кеше запросов. Без сброса ученик проходит
    // тренировку, возвращается и видит СТАРЫЕ числа: выглядит так, будто работа
    // не засчиталась.
    qc.invalidateQueries({ queryKey: ['vocab'] });
    // ⚠️ СВОЕГО ПРАВИЛА «ВЫУЧЕНО» ЗДЕСЬ БОЛЬШЕ НЕТ. Стояло
    // `correct && current.box >= 4` — собственный порог, не совпадавший с
    // сервером ни числом, ни смыслом, и из-за него экран конца захода писал
    // «выучено N», когда словарь показывал 0. Сверх того `current.box` — это
    // ступень способа «карточка», а не того способа, которым отвечали: на
    // карточке «на слух» порог сверялся с чужим счётом.
    //
    // Честное число знает только сервер (правило «засчитаны все пять способов»),
    // и оно приезжает в словарь: кеш сброшен строкой выше. Здесь остаётся то,
    // что клиент знает сам — сколько ответил и сколько верно.
    setStats((s) => ({
      answered: s.answered + 1,
      correct: s.correct + (correct ? 1 : 0),
    }));
    setQueue((q) => {
      const rest = q.slice(1);
      // ⚠️ ПОВТОРЯЕМ ТЕМ ЖЕ СПОСОБОМ, а не карточкой. Раньше ошибка в любом
      // упражнении возвращала слово простой карточкой — теперь у каждого
      // способа свой счёт, и подмена засчитала бы ответ не туда: человек
      // запнулся на «набрать по буквам», а закрылась бы «карточка».
      const next = correct ? rest : [...rest, { ...current, uid: `${current.uid}-retry` }];
      if (next.length === 0) loadBatch();
      return next;
    });
  };

  if (loading && queue.length === 0 && !finished) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
      </div>
    );
  }

  if (finished || !current) {
    return (
      <div className="mx-auto grid min-h-[60vh] max-w-lg place-items-center px-4">
        <Card className="w-full text-center">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-primary-container text-4xl">🎉</div>
          <h2 className="mt-3 text-2xl font-bold">{t('cueTrainer.sessionDone')}</h2>
          <p className="mt-1 text-on-surface-variant">
            {t('cueTrainer.sessionStats', { answered: stats.answered, correct: stats.correct })}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="ghost" onClick={() => navigate(backTo)}>{t(isPersonal ? 'cueTrainer.toVocab' : 'cueTrainer.toSets')}</Button>
            <Button onClick={() => { setFinished(false); setStats({ answered: 0, correct: 0 }); loadBatch(); }}>
              {t('cueTrainer.again')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-4">
      {/* top bar */}
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(backTo)} className="rounded-button p-2 text-on-surface-variant hover:text-on-surface">
          <span className="material-symbols-outlined">close</span>
        </button>
        <div className="h-2.5 flex-1 overflow-hidden rounded-pill bg-surface-container-highest">
          <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, stats.answered * 6)}%` }} />
        </div>
        <Pill variant="neutral">{t('cueTrainer.left', { n: queue.length })}</Pill>
      </div>
      <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        {current.stage_total > 0 && (
          <span className="mr-1.5 rounded-pill bg-primary-fixed px-2 py-0.5 text-on-primary-fixed-variant">
            {t('cueTrainer.stage', { stage: current.stage, total: current.stage_total })}
          </span>
        )}
        {t(MODE_KEY[current.mode] ?? current.mode)}
      </p>
      <CardStage key={current.uid} card={current} onGrade={grade} />
    </div>
  );
}

// One card rendered by its mode, fully interactive. Reports the result via onGrade.
// Пример с пропуском рисуется разбором строки, а НЕ вставкой HTML.
// Раньше здесь стоял dangerouslySetInnerHTML по example_gap — а это поле
// приходит из словаря, куда текст попадает от пользователей и от модели.
// Пример вида `He <img src=x onerror=...> the door` выполнил бы скрипт
// в сессии ученика. Экранирование тут не помогло бы: подставлять надо было
// именно разметку пропуска, поэтому сток убран целиком.
function gapLine(text: string) {
  const parts = text.split('____');
  return parts.flatMap((part, i) =>
    i === 0
      ? [part]
      : [
          <span
            key={`gap-${i}`}
            className="mx-1 inline-block min-w-[70px] border-b-2 border-primary align-bottom"
          >
            &nbsp;
          </span>,
          part,
        ],
  );
}

// Экспортирован ради живой проверки `cueCardVariants.test.ts`.
export function CardStage({ card, onGrade }: { card: SessionCard; onGrade: (correct: boolean, typed?: string) => void }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<number | null>(null);
  const [typed, setTyped] = useState('');
  const [revealed, setRevealed] = useState(false);

  // Страховка на случай, если карточка сменилась, а компонент не пересоздали:
  // без неё «уже отвечено» перетекает на следующую карточку, она открывается
  // с подсвеченным ответом и заблокированными кнопками. Ключ по uid это уже
  // предотвращает, но цена ошибки здесь — намертво зависший тренажёр,
  // поэтому проверяем дважды.
  useEffect(() => {
    setPicked(null);
    setTyped('');
    setRevealed(false);
  }, [card.word_id, card.sense_index, card.mode]);

  // ⚠️ ЗВУК ИДЁТ ОБЩИМ ПРОИГРЫВАТЕЛЕМ, а не своим <audio> на карточку.
  //
  // Своим был до 10.09.2026, и вместе с ним отсутствовал показ загрузки:
  // на плохой связи нажатие выглядело как «ничего не произошло». Теперь тот же
  // путь, что в словаре и в наборе слов, — со значком ожидания и повтором.
  const url = card.audio_url;
  // Карточка «услышал и напечатал» звучит сама: слышать слово — и есть задание.
  useEffect(() => {
    if (card.mode === 'audio' && url) playSrc(url);
  }, [card.word_id, card.mode, url]);

  const speaker = url ? (
    <AudioPlayButton src={url} className="bg-primary-container text-primary rounded-full h-11 w-11" />
  ) : null;

  // ---- card (knowledge): show everything, one "got it" button ----
  if (card.mode === 'card') {
    return (
      <Card className="text-center">
        <div className="flex items-center justify-center gap-3">
          <h3 className="text-3xl font-extrabold tracking-tight">{card.word}</h3>
          {speaker}
        </div>
        <SenseTag card={card} />
        <p className="mt-2 text-lg font-semibold text-primary">{card.translation}</p>
        {card.explanation && <p className="mt-2 text-sm text-on-surface-variant">{card.explanation}</p>}
        {card.example && <p className="mt-3 rounded-card bg-surface-container-low p-3 text-sm">“{card.example}”</p>}
        <Button className="mt-5" onClick={() => onGrade(true)}>✓ {t('cueTrainer.gotIt')}</Button>
      </Card>
    );
  }

  // ---- choose translation / sentence gap: 4 options ----
  if (card.mode === 'choose' || card.mode === 'sentence') {
    const opts = card.options ?? [];
    const pick = (i: number) => {
      if (picked !== null) return;
      tapSelection();
      setPicked(i);
      const correct = i === card.correct_index;
      setTimeout(() => onGrade(correct), 750);
    };
    return (
      <Card>
        {card.mode === 'choose' ? (
          <div className="flex flex-col items-center gap-1 text-center">
            <div className="flex items-center justify-center gap-3">
              <h3 className="text-3xl font-extrabold tracking-tight">{card.word}</h3>
              {speaker}
            </div>
            <SenseTag card={card} />
          </div>
        ) : (
          <p className="text-center text-lg leading-relaxed">{gapLine(card.example_gap || '')}</p>
        )}
        <ul className="mt-4 grid gap-2">
          {opts.map((o, i) => (
            <li key={i}>
              <button
                disabled={picked !== null}
                onClick={() => pick(i)}
                className={cn(
                  'w-full rounded-2xl border border-outline-variant bg-surface px-4 py-3 text-left text-[15px] font-medium transition',
                  picked === null && 'hover:border-primary',
                  picked !== null && i === card.correct_index && 'border-success bg-success-container text-on-success-container',
                  picked === i && i !== card.correct_index && 'border-error bg-error-container text-on-error-container',
                )}
              >
                {o}
              </button>
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  // ---- audio / type: ученик набирает слово ----
  //
  // ⚠️ СВЕРЯЕМ ЧЕРЕЗ isWordAnswerCorrect, А НЕ ПРОСТЫМ РАВЕНСТВОМ. В справочник
  // попадают слова с пометкой куратора — «commitment (noun)». Озвучка говорит
  // просто «commitment»: ученик слышит слово, пишет верно и получает
  // «неправильно», а под ответом ему показывают «Correct: commitment (noun)».
  // Догадаться, что надо было дописать часть речи в скобках, невозможно.
  const ok = isWordAnswerCorrect(typed, card.word);
  const check = () => {
    setRevealed(true);
    // ⚠️ Отдаём НАБРАННОЕ, а не `ok`: решать будет сервер, у которого эталон.
    setTimeout(() => onGrade(ok, typed), 900);
  };
  return (
    <Card className="text-center">
      {card.mode === 'audio' ? (
        <div className="flex flex-col items-center gap-2">
          {url && (
            <AudioPlayButton src={url} big className="bg-primary text-on-primary shadow-ambient" />
          )}
          <p className="text-sm text-on-surface-variant">{t('cueTrainer.listenAndType')}</p>
        </div>
      ) : (
        <>
          <p className="text-lg font-semibold text-primary">{card.translation}</p>
          {card.explanation && <p className="mt-1 text-sm text-on-surface-variant">{card.explanation}</p>}
        </>
      )}
      <input
        autoFocus
        value={typed}
        disabled={revealed}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim() && !revealed) check(); }}
        placeholder={t('cueTrainer.typePlaceholder')}
        className={cn(
          'mt-4 w-full rounded-2xl border-2 bg-surface px-4 py-3 text-center text-lg font-semibold outline-none',
          !revealed && 'border-outline-variant focus:border-primary',
          revealed && ok && 'border-success bg-success-container',
          revealed && !ok && 'border-error bg-error-container',
        )}
      />
      {/* ⚠️ «toast / speech» (договор vocab-alternatives.md): засчитывается любой
          вариант — говорим об этом до проверки. Самих вариантов не называем: в
          режиме «audio» это выдало бы ответ. Эталон после ошибки — как есть, целиком. */}
      {!revealed && hasWordVariants(card.word) && (
        <p className="mt-2 text-xs text-on-surface-variant">{t('cueTrainer.anyVariant')}</p>
      )}
      {revealed && !ok && <p className="mt-2 text-sm text-error">{t('cueTrainer.correctIs')} <b>{card.word}</b></p>}
      {!revealed && (
        <Button className="mt-4" disabled={!typed.trim()} onClick={check}>{t('cueTrainer.check')}</Button>
      )}
    </Card>
  );
}
